import { isValidPhoneNumber } from 'react-phone-number-input';
import {
  NAME_MAX_LENGTH,
  isValidContactEmail,
} from 'helper/companyContactForm';
import { toPhoneValue } from 'helper/companyContactPhone';

// The form of a billing contact without an account (mockups 8.5 and 8.6), as
// BillingContactExternalForm edits it. Field names follow the member forms
// (firstName, lastName); the api reads firstname and lastname.

/** "Prénom NOM", or an empty string. */
export const billingContactName = (person) =>
  [person?.firstname, (person?.lastname ?? '').toUpperCase()]
    .filter(Boolean)
    .join(' ');

export const isBillingRecipient = (person) =>
  Boolean(person?.invoices || person?.reminders);

// A new contact is added to receive something: both boxes start checked.
export const emptyBillingContactForm = ({
  email = '',
  firstName = '',
  lastName = '',
} = {}) => ({
  email,
  firstName,
  lastName,
  phone: '',
  invoices: true,
  reminders: true,
});

/**
 * What the search hands to the new contact form: an address goes to the
 * email, anything else is a name ("Service comptable" is a last name for a
 * single word, a first name then a last name otherwise).
 */
export const billingContactPrefillFrom = (typed) => {
  const value = (typed ?? '').trim();
  if (!value) return {};
  if (value.includes('@')) return { email: value };
  const [first, ...rest] = value.split(/\s+/);
  if (rest.length === 0) return { lastName: first };
  return { firstName: first, lastName: rest.join(' ') };
};

export const billingContactFormFrom = (contact) => ({
  email: contact?.email ?? '',
  firstName: contact?.firstname ?? '',
  lastName: contact?.lastname ?? '',
  // Read through the same correction the api applies, so that a stored +33
  // overseas number does not count as a change once shown with its own code.
  phone: toPhoneValue(contact?.phone) ?? '',
  invoices: contact?.invoices === true,
  reminders: contact?.reminders === true,
});

/**
 * { field: key } of the client-side refusals, keys under companyBillingContacts.errors.
 * `isEmailOptional`: a company's contact may have no address (JBWEB-1484); a
 * billing contact always has one, since it is what receives the invoices.
 */
export const validateBillingContactForm = (form, { isEmailOptional = false } = {}) => {
  const errors = {};
  ['firstName', 'lastName'].forEach((field) => {
    const value = (form[field] ?? '').trim();
    if (!value) errors[field] = 'required';
    else if (value.length > NAME_MAX_LENGTH) errors[field] = 'nameTooLong';
  });
  const email = (form.email ?? '').trim();
  if (!email) {
    if (!isEmailOptional) errors.email = 'required';
  }
  else if (!isValidContactEmail(email)) errors.email = 'emailInvalid';
  if (form.phone && !isValidPhoneNumber(form.phone)) {
    errors.phone = 'phoneInvalid';
  }
  if (!form.invoices && !form.reminders) {
    errors.notifications = 'notificationsEmpty';
  }
  return errors;
};

// Each field as the api stores it: compared that way, retyping the same
// address in capitals or adding a trailing space is not a change.
const API_FIELDS = {
  email: (form) => (form.email ?? '').trim().toLowerCase(),
  firstname: (form) => (form.firstName ?? '').trim(),
  lastname: (form) => (form.lastName ?? '').trim(),
  phone: (form) => form.phone || null,
  invoices: (form) => form.invoices === true,
  reminders: (form) => form.reminders === true,
};

/** The POST body. */
export const toBillingContactPayload = (form) => {
  const payload = Object.fromEntries(
    Object.entries(API_FIELDS).map(([field, read]) => [field, read(form)]),
  );
  if (!payload.phone) delete payload.phone;
  return payload;
};

/** The PUT body: only the fields that differ from the contact as opened. */
export const billingContactChanges = (initialForm, form) =>
  Object.fromEntries(
    Object.entries(API_FIELDS)
      .filter(([, read]) => read(initialForm) !== read(form))
      .map(([field, read]) => [field, read(form)]),
  );

// The api refusals that belong to one field: [field, key under errors].
export const BILLING_CONTACT_FIELD_ERRORS = {
  EMAIL_INVALID: ['email', 'emailInvalid'],
  FIRSTNAME_INVALID: ['firstName', 'required'],
  LASTNAME_INVALID: ['lastName', 'required'],
  PHONE_INVALID: ['phone', 'phoneInvalid'],
  CONTACT_IS_MEMBER: ['email', 'contactIsMember'],
  BILLING_CONTACT_EXISTS: ['email', 'contactExists'],
  NOTIFICATIONS_EMPTY: ['notifications', 'notificationsEmpty'],
  NOTIFICATIONS_INVALID: ['notifications', 'notificationsInvalid'],
};

/**
 * What « Inviter sur Jobbiz » hands to the invitation modal (1262). The boxes
 * go as levels, so the invited member keeps receiving what was ticked here.
 */
export const invitationPrefillFrom = (form) => ({
  email: (form.email ?? '').trim(),
  firstName: (form.firstName ?? '').trim(),
  lastName: (form.lastName ?? '').trim(),
  phone: form.phone ?? '',
  notifications: {
    invoices: form.invoices ? 'all' : 'none',
    reminders: form.reminders ? 'all' : 'none',
  },
});

// A contact of the company without an account (ModalAddCompanyContact,
// JBWEB-1424): the billing contact's form and rules, without its boxes.
export const emptyContactForm = (values) => ({ ...emptyBillingContactForm(values), position: '', invoices: false, reminders: false });

export const contactFormFrom = (contact) => ({
  ...billingContactFormFrom(contact),
  position: contact?.position ?? '',
  invoices: false,
  reminders: false,
});

// The position is required for a company's contact (decision 10), never for a
// billing contact.
export const validateContactForm = (form, options) => {
  const { notifications, ...errors } = validateBillingContactForm(form, options);
  if (!(form.position ?? '').trim()) errors.position = 'required';
  return errors;
};

export const toContactPayload = (form) => {
  const { invoices, reminders, ...payload } = toBillingContactPayload(form);
  return { ...payload, position: (form.position ?? '').trim() };
};
